import { FocusTrap } from 'focus-trap-react';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { CSSTransition } from 'react-transition-group';

import {
  ViewWrapper,
  ViewWrapperDefaultGrid,
} from '@xing-com/platform-layout-view-wrapper';
import { Portal } from '@xing-com/portal';

import * as Styled from './modal-page.styles';
import type { ModalPageProps } from './modal-page.types';

const { modalPageDuration, modalPageTransitionName } = Styled;

export const ModalPage = React.forwardRef<HTMLDivElement, ModalPageProps>(
  (
    {
      children,
      closeButton,
      contentClassName,
      focusTrapProps,
      isAnimated,
      isShown,
      noGrid,
      noMargin,
      zIndex,
      ...props
    },
    forwardedRef
  ): JSX.Element => {
    const intl = useIntl();
    const modalRef = React.useRef<HTMLDivElement>(null);
    const resolvedRef = forwardedRef || modalRef;

    const animatedModalPageRef = React.useRef<HTMLDivElement>(null);
    const focusTrapContainerRef = React.useRef<HTMLDivElement>(null);

    // Prevent background scrolling
    React.useEffect(() => {
      if (isShown && isAnimated) {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
          document.body.style.overflow = originalOverflow;
        };
      }

      return undefined;
    }, [isShown, isAnimated]);

    // Keyboard handling
    React.useEffect(() => {
      const handleKey = (event: KeyboardEvent): void => {
        if (isShown && closeButton && event.key === 'Escape') {
          event.preventDefault();
          closeButton.onClick?.();
        }
      };

      document.addEventListener('keydown', handleKey);

      return () => {
        document.removeEventListener('keydown', handleKey);
      };
    }, [closeButton, isShown]);

    const { focusTrapOptions: userFocusTrapOptions, ...otherFocusTrapProps } =
      focusTrapProps || {};
    const mergedFocusTrapOptions = {
      escapeDeactivates: Boolean(closeButton),
      fallbackFocus: () => focusTrapContainerRef.current || document.body,
      returnFocusOnDeactivate: true,
      ...(userFocusTrapOptions || {}),
    };

    const content = (
      <ViewWrapper
        contentClassName={contentClassName}
        data-xds="ModalPage"
        {...props}
      >
        {closeButton ? (
          <Styled.CloseButton
            aria-label={intl.formatMessage({
              id: 'BUTTON_CLOSEWINDOW',
              defaultMessage: 'Close window',
            })}
            data-qa="close"
            data-testid="close"
            size="large"
            {...closeButton}
          />
        ) : undefined}
        <Styled.Container
          $noMargin={noMargin}
          aria-modal={isAnimated ? 'true' : undefined}
          ref={resolvedRef}
          role={isAnimated ? 'dialog' : undefined}
          tabIndex={isAnimated ? -1 : undefined}
        >
          {children}
        </Styled.Container>
      </ViewWrapper>
    );

    const wrappedContent = noGrid ? (
      content
    ) : (
      <ViewWrapperDefaultGrid>{content}</ViewWrapperDefaultGrid>
    );

    if (isAnimated) {
      return (
        <Portal>
          <CSSTransition
            appear={isShown}
            classNames={modalPageTransitionName}
            in={isShown}
            key="modalPage"
            mountOnEnter
            timeout={parseInt(modalPageDuration)}
            unmountOnExit
            nodeRef={animatedModalPageRef}
          >
            <Styled.AnimatedModalPage
              ref={animatedModalPageRef}
              $zIndex={zIndex}
            >
              <FocusTrap
                active={isShown}
                {...otherFocusTrapProps}
                focusTrapOptions={mergedFocusTrapOptions}
              >
                <div ref={focusTrapContainerRef} tabIndex={-1}>
                  {wrappedContent}
                </div>
              </FocusTrap>
            </Styled.AnimatedModalPage>
          </CSSTransition>
        </Portal>
      );
    }

    return wrappedContent;
  }
);

ModalPage.displayName = 'ModalPage';
